import React, { useEffect, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { IconButton, Text, Snackbar } from "react-native-paper";
import { useTranslation } from "react-i18next";
import { listenCompanyDocuments } from "../../services/firestoreServices";
import { Folder, File } from "./types";
import FolderCard from "./components/FolderCard";
import FileListItem from "./components/FileListItem";
import CreateFolderDialog from "./components/CreateFolderDialog";
import DriveFAB from "./components/DriveFAB";
import { useFolderManagement } from "./hooks/useFolderManagement";
import { useFileManagement } from "./hooks/useFileManagement";
import { useFileUpload } from "./hooks/useFileUpload";
import { downloadDriveFile } from "./utils/downloadDriveFile";
import { useAppSelector } from "../../redux/store";
import RenameFileDialog from "./components/RenameFileDialog";

interface FolderContentsScreenProps {
  navigation: any;
  route: any;
}

export default function FolderContentsScreen({
  navigation,
  route,
}: FolderContentsScreenProps) {
  const folder = route.params?.folder;
  const [subFolders, setSubFolders] = useState<Folder[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const { t } = useTranslation();

  const company = useAppSelector((state) => state.auth.user.company);

  const folderManagement = useFolderManagement({
    existingFolders: subFolders,
    parentFolderId: folder.id,
  });
  const fileManagement = useFileManagement({});

  const fileUpload = useFileUpload({
    folderId: folder.id,
    companyId: company?.id || "",
  });

  useEffect(() => {
    navigation.setOptions({
      title: folder.name,
    });
  }, [navigation, folder.name]);

  // Listen to subfolders from Firestore
  useEffect(() => {
    const unsubscribe = listenCompanyDocuments("drive_folders", setSubFolders, {
      where: [
        {
          field: "parentFolderId",
          operator: "==",
          value: folder.id,
        },
      ],
    });

    return () => unsubscribe?.();
  }, [folder.id]);

  // Listen to files from Firestore
  useEffect(() => {
    const unsubscribe = listenCompanyDocuments("drive_files", setFiles, {
      where: [
        {
          field: "folderId",
          operator: "==",
          value: folder.id,
        },
      ],
    });

    return () => unsubscribe?.();
  }, [folder.id]);

  const handleFolderPress = (selectedFolder: Folder) => {
    navigation.push("FolderContents", { folder: selectedFolder });
  };

  const handleFileOpen = async (file: File) => {
    try {
      await downloadDriveFile(file);
    } catch (error) {
      console.error("Failed to open file:", error);
    }
  };

  const handleFileShare = (file: File) => {
    console.log("Share file", file.id);
  };

  const handleFileOpenDetails = (file: File) => {
    navigation.navigate("FileDetails", { file });
  };

  const handleUploadFile = async () => {
    await fileUpload.pickAndUploadFiles();
    if (fileUpload.error) {
      setSnackbarVisible(true);
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView style={styles.content}>
        {subFolders.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text variant="labelLarge" style={styles.sectionTitle}>
                {t("Folders")}
              </Text>
            </View>

            <View style={styles.foldersGrid}>
              {subFolders.map((subFolder) => (
                <FolderCard
                  key={subFolder.id}
                  folder={subFolder}
                  onPress={handleFolderPress}
                  onEdit={folderManagement.openEditDialog}
                  onDelete={folderManagement.openDeleteDialog}
                />
              ))}
            </View>
          </View>
        )}

        {files.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text variant="labelLarge" style={styles.sectionTitle}>
                {t("Files")}
              </Text>
            </View>

            <View style={styles.filesList}>
              {files.map((file) => (
                <FileListItem
                  key={file.id}
                  file={file}
                  onOpen={handleFileOpen}
                  onDownload={handleFileOpen}
                  onShare={handleFileShare}
                  onRename={fileManagement.openRenameDialog}
                  onDelete={fileManagement.openDeleteDialog}
                  onViewDetails={handleFileOpenDetails}
                />
              ))}
            </View>
          </View>
        )}

        {subFolders.length === 0 && files.length === 0 && (
          <View style={styles.emptyState}>
            <IconButton icon="folder-open-outline" size={64} />
            <Text variant="titleLarge" style={styles.emptyTitle}>
              This folder is empty
            </Text>
            <Text variant="bodyMedium" style={styles.emptyDescription}>
              Add folders or upload files to get started
            </Text>
          </View>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>

      <CreateFolderDialog
        visible={folderManagement.dialogVisible}
        folderName={folderManagement.folderName}
        error={folderManagement.error}
        mode={folderManagement.editingFolder ? "edit" : "create"}
        onChangeName={folderManagement.handleNameChange}
        onConfirm={folderManagement.saveFolder}
        onCancel={folderManagement.closeNameDialog}
      />

      <DriveFAB
        onCreateFolder={folderManagement.openCreateDialog}
        onUploadFile={handleUploadFile}
      />

      <RenameFileDialog
        visible={fileManagement.renameDialogVisible}
        fileName={fileManagement.fileName}
        error={fileManagement.renameError}
        onChangeName={fileManagement.handleFileNameChange}
        onConfirm={fileManagement.saveRenamedFile}
        onCancel={fileManagement.closeRenameDialog}
      />

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
      >
        {fileUpload.error}
      </Snackbar>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  section: {
    marginTop: 8,
    paddingHorizontal: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: "600",
    opacity: 0.6,
  },
  foldersGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  filesList: {
    gap: 4,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 64,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontWeight: "600",
    marginTop: 16,
    marginBottom: 8,
    textAlign: "center",
  },
  emptyDescription: {
    opacity: 0.6,
    textAlign: "center",
  },
  bottomSpacer: {
    height: 120,
  },
});
